const API_URL = (import.meta as any).env.VITE_API_URL || 'http://localhost:3035';

class ApiClient {
  private baseUrl: string;
  private token: string | null;

  constructor() {
    this.baseUrl = API_URL;
    this.token = localStorage.getItem('token');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Scheduling Pages
  async getSchedulingPages() {
    return this.request('/api/v1/scheduling/pages');
  }

  async getSchedulingPage(id: string) {
    return this.request(`/api/v1/scheduling/pages/${id}`);
  }

  async createSchedulingPage(data: any) {
    return this.request('/api/v1/scheduling/pages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSchedulingPage(id: string, data: any) {
    return this.request(`/api/v1/scheduling/pages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSchedulingPage(id: string) {
    return this.request(`/api/v1/scheduling/pages/${id}`, {
      method: 'DELETE',
    });
  }

  // Availability
  async getAvailability(pageId: string, startDate: string, endDate: string) {
    return this.request(`/api/v1/scheduling/pages/${pageId}/availability?startDate=${startDate}&endDate=${endDate}`);
  }

  async addAvailabilityRule(pageId: string, data: any) {
    return this.request(`/api/v1/scheduling/pages/${pageId}/availability/rules`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAvailabilityRule(pageId: string, ruleId: string) {
    return this.request(`/api/v1/scheduling/pages/${pageId}/availability/rules/${ruleId}`, {
      method: 'DELETE',
    });
  }

  // Bookings
  async getBookings() {
    return this.request('/api/v1/scheduling/bookings');
  }

  async getBooking(id: string) {
    return this.request(`/api/v1/scheduling/bookings/${id}`);
  }

  async cancelBooking(id: string, reason?: string) {
    return this.request(`/api/v1/scheduling/bookings/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // Google Calendar connect: send the connect_ticket from the return page. The
  // backend forwards it as the signed-in user.
  async completeCalendarConnect(ticket: string) {
    return this.request<{ success: boolean }>('/api/v1/calendar/google/complete', {
      method: 'POST',
      body: JSON.stringify({ ticket }),
    });
  }

  // Public API (no auth required)
  async getPublicPage(slug: string) {
    return fetch(`${this.baseUrl}/api/v1/scheduling/public/${slug}`).then(r => r.json());
  }

  async getPublicAvailability(slug: string, startDate: string, endDate: string) {
    return fetch(`${this.baseUrl}/api/v1/scheduling/public/${slug}/availability?startDate=${startDate}&endDate=${endDate}`).then(r => r.json());
  }

  async createBooking(slug: string, data: any) {
    return fetch(`${this.baseUrl}/api/v1/scheduling/public/${slug}/book`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(r => r.json());
  }

  // One-Time Links
  async generateOneTimeLink(pageId: string, data: { expiresInHours?: number }) {
    return this.request(`/api/v1/one-time-link/generate/${pageId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getOneTimeLinkStatus(pageId: string) {
    return this.request(`/api/v1/one-time-link/status/${pageId}`);
  }

  async revokeOneTimeLink(pageId: string) {
    return this.request(`/api/v1/one-time-link/revoke/${pageId}`, {
      method: 'DELETE',
    });
  }

  // Webhooks
  async getWebhooks(pageId: string) {
    return this.request(`/api/v1/webhooks?pageId=${pageId}`);
  }

  async createWebhook(pageId: string, data: any) {
    return this.request('/api/v1/webhooks', {
      method: 'POST',
      body: JSON.stringify({ ...data, pageId }),
    });
  }

  async deleteWebhook(webhookId: string) {
    return this.request(`/api/v1/webhooks/${webhookId}`, {
      method: 'DELETE',
    });
  }

  // Meeting Templates
  async getMeetingTemplates() {
    return this.request('/api/v1/templates');
  }

  async getMeetingTemplate(id: string) {
    return this.request(`/api/v1/templates/${id}`);
  }

  async createMeetingTemplate(data: any) {
    return this.request('/api/v1/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMeetingTemplate(id: string, data: any) {
    return this.request(`/api/v1/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteMeetingTemplate(id: string) {
    return this.request(`/api/v1/templates/${id}`, {
      method: 'DELETE',
    });
  }

  async applyTemplateToPage(templateId: string, pageId: string) {
    return this.request(`/api/v1/templates/${templateId}/apply/${pageId}`, {
      method: 'POST',
    });
  }

  // Customer Portal
  async createPortalSession(email: string) {
    return this.request('/api/v1/portal/session', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async getCustomerBookings(token: string) {
    return this.request(`/api/v1/portal/bookings?token=${token}`);
  }

  async cancelCustomerBooking(token: string, bookingId: string, reason?: string) {
    return this.request(`/api/v1/portal/bookings/${bookingId}/cancel?token=${token}`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async requestReschedule(token: string, bookingId: string) {
    return this.request(`/api/v1/portal/bookings/${bookingId}/reschedule?token=${token}`, {
      method: 'POST',
    });
  }

  // Eureka Projects
  getEurekaProjects() {
    return this.request<{ projects: { id: string; name: string }[] }>('/api/v1/user/projects');
  }

  setToken(token: string) {
    this.token = token;
  }

  // CRM Integrations
  getCrmIntegrations() {
    return this.request('/api/v1/crm/integrations');
  }

  createCrmIntegration(data: { provider?: string; eurekaProjectId?: string; eurekaEntityType?: string; logActivity?: boolean }) {
    return this.request('/api/v1/crm/integrations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  deleteCrmIntegration(id: string) {
    return this.request(`/api/v1/crm/integrations/${id}`, {
      method: 'DELETE',
    });
  }

  testCrmConnection(id: string) {
    return this.request(`/api/v1/crm/integrations/${id}/test`, {
      method: 'POST',
    });
  }
}

export const api = new ApiClient();
